import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Logo from "@/mockups/Logo";
import { Button } from "@/components/ui/button";
import { Building2, FolderKanban, Clock, CheckSquare, ArrowRight, AlertCircle, FlaskConical } from "lucide-react";

const PREVIEW_ROLES = ["Directora", "Administración", "Cuentas", "Diseñador"];
const isPreview =
  typeof window !== "undefined" && window.location.host.endsWith(".preview.emergentagent.com");

export default function Login() {
  const { user, loading, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState("");
  const [devBusy, setDevBusy] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem("auth_error");
      if (msg) { setAuthError(msg); sessionStorage.removeItem("auth_error"); }
    } catch (_) {}
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDevLogin = async (role) => {
    setDevBusy(role);
    setAuthError("");
    try {
      const res = await api.post("/auth/dev-login", { role });
      setUser(res.data.user);
      await checkAuth();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo entrar como " + role + ".";
      setAuthError(msg);
    } finally {
      setDevBusy("");
    }
  };

  const features = [
    { icon: Building2, label: "CRM · Clientes, contactos y proveedores" },
    { icon: FolderKanban, label: "Proyectos con hitos y tablero Kanban" },
    { icon: CheckSquare, label: "Tareas con estados y asignaciones" },
    { icon: Clock, label: "Seguimiento de horas y rentabilidad" },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: brand + login */}
      <div className="flex flex-col justify-center px-8 sm:px-14 lg:px-20 py-12">
        <div className="mb-16">
          <Logo variant="dark" className="h-7" />
        </div>

        <p className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase mb-4">
          Gestión integral de agencia
        </p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-5">
          Un único lugar para tu CRM, proyectos y horas.
        </h1>
        <p className="text-muted-foreground text-base max-w-md mb-10 leading-relaxed">
          Centraliza clientes, planifica proyectos con tableros Kanban y controla la rentabilidad
          en tiempo real.
        </p>

        <Button data-testid="google-login-btn" onClick={handleLogin} size="lg" className="w-full sm:w-auto group h-12 px-6 text-base gap-2">
          <GoogleIcon />
          Continuar con Google
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Button>

        {authError && (
          <div data-testid="auth-error" className="mt-6 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3.5 text-sm text-red-800 max-w-md dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            <AlertCircle className="h-4.5 w-4.5 shrink-0 mt-0.5" />
            <span className="leading-relaxed">{authError}</span>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-6">
          Acceso restringido a las cuentas autorizadas de <strong>@grabarte360.com</strong>. Si necesitas acceso, contacta con Administración.
        </p>

        {isPreview && (
          <div data-testid="dev-login-panel" className="mt-8 max-w-md rounded-xl border border-dashed border-gold/50 bg-gold/5 p-4">
            <div className="flex items-center gap-2 mb-1">
              <FlaskConical className="h-4 w-4 text-gold" />
              <span className="text-xs font-semibold tracking-wide uppercase text-foreground">
                Acceso de prueba (solo preview)
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              Entra directamente con un rol para revisar la app. Solo funciona aquí en preview; en producción se entra con Google.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {PREVIEW_ROLES.map((role) => (
                <Button
                  key={role}
                  data-testid={`dev-login-${role}`}
                  onClick={() => handleDevLogin(role)}
                  disabled={!!devBusy}
                  variant="outline"
                  size="sm"
                  className="justify-start h-9 text-sm"
                >
                  {devBusy === role ? "Entrando…" : role}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: feature panel */}
      <div className="hidden lg:flex flex-col justify-center bg-navy text-white px-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-md">
          <Logo variant="light" className="h-6 mb-10" />
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-white/40 mb-8">Todo integrado</p>
          <div className="space-y-5">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-4 animate-fade-in-up" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="h-11 w-11 rounded-md bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5 text-gold" />
                </div>
                <span className="text-[15px] text-white/90">{f.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-8 border-t border-white/15 flex gap-8">
            <div>
              <div className="font-heading font-bold text-3xl tabular-nums text-gold">15+</div>
              <div className="text-xs text-white/50 mt-1">Usuarios del equipo</div>
            </div>
            <div>
              <div className="font-heading font-bold text-3xl tabular-nums text-gold">5</div>
              <div className="text-xs text-white/50 mt-1">Roles con permisos</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path fill="#fff" d="M12 10.2v3.9h5.4c-.24 1.4-1.7 4.1-5.4 4.1-3.25 0-5.9-2.7-5.9-6s2.65-6 5.9-6c1.85 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.6 14.7 2.7 12 2.7 6.9 2.7 2.8 6.8 2.8 12S6.9 21.3 12 21.3c5.3 0 8.8-3.7 8.8-8.9 0-.6-.06-1.05-.15-1.5H12z"/>
    </svg>
  );
}
